import { useQuery } from '@tanstack/react-query'
import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { BarChart3 } from 'lucide-react'
import apiClient from '@/lib/api/client'
import Skeleton from '@/components/ui/Skeleton'
import RegionGrid from './RegionGrid'
import FacilityTypeChart from './FacilityTypeChart'
import type { FacilityStatisticsResponse } from '@/types/statistics'

function useStatistics() {
  return useQuery({
    queryKey: ['facilityStatistics'],
    queryFn: async () => {
      const res = await apiClient.get('/facilities/statistics')
      return res.data.data as FacilityStatisticsResponse
    },
    staleTime: 60 * 60 * 1000, // 1시간 캐시
  })
}

export default function StatisticsSection() {
  const { data, isLoading } = useStatistics()
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-50px' })

  if (isLoading) {
    return (
      <div className="px-5 py-6 lg:max-w-6xl lg:mx-auto lg:px-6 lg:py-16">
        <Skeleton height="1.5rem" width="40%" />
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          <Skeleton height="300px" />
          <Skeleton height="300px" />
        </div>
      </div>
    )
  }

  if (!data) return null

  return (
    <section ref={ref} className="lg:bg-white lg:py-16">
      <div className="lg:max-w-6xl lg:mx-auto lg:px-6">
        {/* ── 데스크톱 헤더 (모바일 미노출) ── */}
        <div className="hidden lg:flex items-center gap-3 mb-10">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cb-primary/10">
            <BarChart3 size={20} className="text-cb-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">전국 시설 현황</h2>
            <p className="text-sm text-gray-500">
              전국{' '}
              <motion.span
                className="font-bold text-cb-primary"
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : { opacity: 0 }}
              >
                {data.totalCount.toLocaleString()}
              </motion.span>
              개 시설 정보를 제공합니다
            </p>
          </div>
        </div>

        {/* ── 모바일 헤더 (데스크톱 미노출) ── */}
        <div className="lg:hidden">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={18} className="text-cb-primary" />
            <h2 className="text-h3 font-bold text-cb-gray-900">전국 시설 현황</h2>
          </div>
          <p className="text-caption text-cb-gray-500 mb-4">
            전국{' '}
            <span className="font-bold text-cb-primary">{data.totalCount.toLocaleString()}</span>
            개 시설
          </p>
        </div>

        {/* ── 그리드: 데스크톱 2열, 모바일 1열 ── */}
        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2 lg:gap-10">
          <div>
            <h3 className="text-body-sm font-bold text-cb-gray-800 mb-3 lg:text-base lg:mb-4">
              <span className="lg:hidden">지역별</span>
              <span className="hidden lg:inline">지역별 시설 수</span>
            </h3>
            <RegionGrid data={data.byRegion} />
            <p className="mt-3 text-[11px] text-gray-400 hidden lg:block">
              클릭하면 해당 지역 시설을 검색합니다
            </p>
          </div>
          <div>
            <h3 className="text-body-sm font-bold text-cb-gray-800 mb-3 lg:text-base lg:mb-4">
              <span className="lg:hidden">시설 유형별</span>
              <span className="hidden lg:inline">시설 유형별 분포</span>
            </h3>
            <FacilityTypeChart data={data.byType} />
          </div>
        </div>
      </div>
    </section>
  )
}
